import { LinkUrlField } from '@/components/admin/liens/link-url-field';
import { DeleteConfirm } from '@/components/admin/reglages/reference-list';
import { RoleLinkForm } from '@/components/admin/vitrine/role-link-form';
import { Button } from '@/components/ui/button';
import type { LinkFormDraft } from '@/lib/link-form';
import { linkEntryKeys, linkFormOf } from '@/lib/link-form';
import {
  LINK_STATE_LABEL,
  linkDeadlineSentence,
  linkUrl,
  revocationWarning,
  sessionCountLabel,
} from '@/lib/link-labels';
import { pinIsBehind, pinWord } from '@/lib/references';
import { useIsVitrine } from '@/lib/use-instance';
import type { ExperienceCard, LinkEntryProblem, LinkSummary } from '@matter_extractor/shared';
import { useState } from 'react';

/**
 * One link, as a card of two rows (§11.4, planche `ReglagesPartage`).
 *
 * Top: the name, the state, the deadline with « Prolonger », the session count.
 * Bottom: the entries as composed pastilles (experience, the version it plays,
 * the side it plays on - « Le Train · latest · côté prod » - and, on a pinned
 * entry that is behind, the click that brings it up to date), the secret
 * URL, « Modifier » and « Révoquer ».
 *
 * ## A revoked or expired link stays, GREYED
 *
 * « C'est la seule trace de ce qu'il a joué » (§11.4). Grey is on the card's
 * ground and its ink, never on the URL field - the URL of a dead link is still
 * what somebody holds, and an operator matching a complaint to a link needs to
 * read it.
 *
 * What goes away are the buttons that could do nothing. A REVOKED link keeps
 * none of the three - it never reopens, so there is nothing to prolong, nothing
 * worth editing and nothing left to revoke - which is exactly what the planche
 * draws. An EXPIRED one keeps all three, and that is not an inconsistency: one
 * click on « Prolonger » brings it back, so editing it before or after that
 * click is the same gesture, and revoking it is how an operator says « and it
 * does not come back ».
 *
 * ## The revocation carries a confirmation, INLINE
 *
 * §11.4 does not ask for one, and it is added anyway: a revocation is the one
 * irreversible gesture of this screen - a link is never un-revoked - and the
 * screens beside it confirm far less (deleting a cran, deleting a tone). Inline
 * under the card and not a modal, at the régime of `reference-list.tsx`: the
 * sentence is about the card it sits under, and a modal would take that card
 * off the screen at the moment the operator is deciding about it.
 */

export interface LinkCardProps {
  readonly link: LinkSummary;
  readonly origin: string;
  readonly now: Date;
  readonly experiences: readonly ExperienceCard[];
  readonly experiencesRead: boolean;
  /** True while one of this card's own writes is in flight. */
  readonly busy: boolean;
  /** The rows the server refused on THIS card's last save. */
  readonly serverProblems: readonly LinkEntryProblem[];
  readonly failure: string | null;
  readonly editing: boolean;
  readonly onEdit: () => void;
  readonly onCancelEdit: () => void;
  readonly onSave: (draft: LinkFormDraft) => void;
  /** « Mettre à jour » on a pinned entry that is behind: the entry at `index` now pins `pin`. */
  readonly onUpdatePin: (index: number, pin: number) => void;
  readonly onExtend: () => void;
  readonly onRevoke: () => void;
}

export function LinkCard({
  link,
  origin,
  now,
  experiences,
  experiencesRead,
  busy,
  serverProblems,
  failure,
  editing,
  onEdit,
  onCancelEdit,
  onSave,
  onUpdatePin,
  onExtend,
  onRevoke,
}: LinkCardProps) {
  const [confirming, setConfirming] = useState(false);
  const revoked = link.state === 'revoked';
  const dead = link.state !== 'active';
  const entryKeys = linkEntryKeys(link.entries);
  // On the vitrine an entry plays the latest version in production: its
  // pastille says that version (« v12 »), never a tag (docs/specs/vitrine.md §7).
  const vitrine = useIsVitrine();
  const playedBy = (experienceId: string): string => {
    const number = experiences.find((card) => card.id === experienceId)?.production?.number;
    return number === undefined ? '' : `v${number}`;
  };

  return (
    <li
      data-testid="partage-lien"
      data-state={link.state}
      className={`space-y-3 rounded-md border-2 p-4 ${dead ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-white'}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3
          data-testid="partage-lien-nom"
          className={`font-bold text-base ${dead ? 'text-gray-600' : 'text-bleu-nuit'}`}
        >
          {link.name}
        </h3>
        <span
          data-testid="partage-lien-etat"
          className={`rounded-sm px-2 py-0.5 font-semibold text-xs ${
            link.state === 'active' ? 'bg-vert-tint text-vert-deep' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {LINK_STATE_LABEL[link.state]}
        </span>
        <span data-testid="partage-lien-echeance" className="text-gray-600 text-sm">
          {linkDeadlineSentence(link, now)}
        </span>
        {revoked ? null : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="partage-lien-prolonger"
            disabled={busy}
            onClick={onExtend}
          >
            Prolonger
          </Button>
        )}
        <span data-testid="partage-lien-sessions" className="ml-auto text-gray-600 text-sm">
          {sessionCountLabel(link.sessionCount)}
        </span>
      </div>

      {editing ? (
        <RoleLinkForm
          // A REMOUNT is how this form is reset (see `link-form.tsx`), and the
          // key carries the state a save may have moved.
          key={`edition-${link.id}-${link.expiresAt}-${link.revokedAt ?? ''}`}
          initial={linkFormOf(link)}
          experiences={experiences}
          experiencesRead={experiencesRead}
          submitLabel="Enregistrer les modifications"
          submitting={busy}
          serverProblems={serverProblems}
          failure={failure}
          testId="partage-formulaire-edition"
          onSubmit={onSave}
          onCancel={onCancelEdit}
        />
      ) : (
        <>
          <ul className="flex flex-wrap gap-2">
            {link.entries.map((entry, index) => {
              const latest = experiences.find((one) => one.id === entry.experienceId);
              const behind =
                latest !== undefined &&
                !entry.retired &&
                pinIsBehind(entry.pin, latest.development.number);
              return (
                <li
                  // The nth occurrence of a (experience, version, side) triple -
                  // a real identity, where the index is only a position. A link
                  // may carry the same triple twice, so the triple alone is not
                  // one.
                  key={entryKeys[index]}
                  data-testid="partage-lien-entree"
                  className="inline-flex flex-wrap items-center gap-1.5 rounded-sm border border-gray-200 bg-gray-50 px-2 py-1 text-xs"
                >
                  <span className={entry.retired ? 'text-gray-500 line-through' : 'text-gray-800'}>
                    {entry.experienceName ?? 'Expérience supprimée'}
                  </span>
                  <span
                    data-testid="partage-lien-entree-version"
                    className="rounded-sm bg-bleu-nuit-tint px-1.5 py-0.5 font-semibold text-bleu-nuit"
                  >
                    {vitrine ? playedBy(entry.experienceId) : pinWord(entry.pin)}
                  </span>
                  {vitrine ? null : (
                    <span
                      data-testid="partage-lien-entree-cote"
                      data-side={entry.side}
                      className={`rounded-sm px-1.5 py-0.5 font-semibold ${
                        entry.side === 'prod'
                          ? 'bg-vert-tint text-vert-deep'
                          : 'bg-bleu-ciel-tint text-bleu-ciel-deep'
                      }`}
                    >
                      côté {entry.side}
                    </span>
                  )}
                  {!vitrine && behind && latest !== undefined ? (
                    <span className="text-jaune-deep">
                      une v{latest.development.number} existe ·{' '}
                      <button
                        type="button"
                        data-testid="partage-lien-entree-maj"
                        disabled={busy}
                        onClick={() => onUpdatePin(index, latest.development.number)}
                        className="cursor-pointer font-semibold text-bleu-nuit underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default"
                      >
                        Mettre à jour
                      </button>
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>

          <LinkUrlField url={linkUrl(origin, link.secret)} label={`URL du lien ${link.name}`} />

          {failure === null ? null : (
            <p role="alert" className="text-rouge-deep text-sm">
              {failure}
            </p>
          )}

          <div className="flex items-center gap-2">
            {revoked ? null : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="partage-lien-modifier"
                disabled={busy}
                onClick={onEdit}
              >
                Modifier
              </Button>
            )}
            {revoked ? null : (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                data-testid="partage-lien-revoquer"
                disabled={busy}
                onClick={() => setConfirming(true)}
              >
                Révoquer
              </Button>
            )}
          </div>

          {confirming ? (
            <DeleteConfirm
              testId="partage-lien-confirmation"
              label={`Révoquer le lien ${link.name}`}
              busy={busy}
              confirmLabel="Révoquer"
              onCancel={() => setConfirming(false)}
              onConfirm={() => {
                setConfirming(false);
                onRevoke();
              }}
            >
              Ce lien rendra l’écran mort à qui l’ouvrira, et il ne se rouvre pas.{' '}
              {revocationWarning(link.sessionCount)}
            </DeleteConfirm>
          ) : null}
        </>
      )}
    </li>
  );
}
